import dagre from '@dagrejs/dagre';
import type { Edge, Node, NodeTypes } from '@xyflow/react';
import {
  Background,
  BackgroundVariant,
  ConnectionMode,
  Controls,
  Handle,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { CheckCircle2, Circle, Loader2, Repeat, Workflow, X } from 'lucide-react';
import React, { useCallback, useMemo } from 'react';

interface Task {
  id: string;
  title: string;
  description: string;
  status: 'not_started' | 'scheduled' | 'in_progress' | 'completed' | 'failed';
  input?: any;
  output?: any;
  startTime?: string;
  endTime?: string;
}

interface SidebarItemData {
  id: string;
  title: string;
  description: string;
  status: 'not_started' | 'scheduled' | 'in_progress' | 'completed' | 'failed';
  input?: any;
  output?: any;
}

interface FlowVisualizationProps {
  workflowRun: {
    id: string;
    workflowId: string;
    status: string;
    startTime: string;
    endTime?: string;
    tasks: Task[];
    workflowName: string;
    workflowPath: string;
    workflowInput?: any;
    workflowOutput?: any;
    workflowDescription?: string;
    workflowFlowChart?: string;
  };
  activeItem: SidebarItemData;
  onItemClick: (item: SidebarItemData) => void;
}

// Custom WorkflowNode component
const WorkflowNode = ({ data }: { data: any }) => (
  <div
    className={`px-4 py-3 rounded-lg border-2 min-w-[200px] cursor-pointer transition-all relative ${
      data.isActive
        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 shadow-lg'
        : 'border-purple-200 dark:border-purple-800 bg-white dark:bg-zinc-900 hover:border-purple-300 dark:hover:border-purple-700 hover:shadow-md'
    }`}
  >
    <div className="flex items-center gap-2">
      <div className="bg-purple-50 dark:bg-purple-900/30 p-1.5 rounded-md">
        <Workflow size={16} className="text-purple-500 dark:text-purple-400" />
      </div>
      <div className="flex-1">
        <div className="font-medium text-sm text-zinc-900 dark:text-zinc-100">{data.title}</div>
        <div className="text-xs text-zinc-600 dark:text-zinc-400 truncate">{data.description}</div>
      </div>
    </div>

    {/* Output handle at bottom */}
    <Handle
      type="source"
      position={Position.Bottom}
      style={{ bottom: -5, background: '#6b7280' }}
    />
  </div>
);

// Custom TaskNode component
const TaskNode = ({ data }: { data: any }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'border-green-500 bg-green-50 dark:bg-green-900/30';
      case 'in_progress':
        return 'border-blue-500 bg-blue-50 dark:bg-blue-900/30';
      case 'scheduled':
        return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/30';
      case 'failed':
        return 'border-red-500 bg-red-50 dark:bg-red-900/30';
      case 'not_started':
        return 'border-gray-300 bg-gray-100 dark:border-gray-600 dark:bg-gray-800';
      default:
        return 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-zinc-900';
    }
  };

  const getTextOpacity = (status: string) => {
    return status === 'not_started' ? 'opacity-50' : 'opacity-100';
  };

  const StatusIcon = () => {
    switch (data.status) {
      case 'completed':
        return <CheckCircle2 size={16} className="text-green-500 dark:text-green-400" />;
      case 'in_progress':
        return <Loader2 size={16} className="text-blue-500 dark:text-blue-400 animate-spin" />;
      case 'scheduled':
        return <Loader2 size={16} className="text-yellow-500 dark:text-yellow-400 animate-spin" />;
      case 'failed':
        return <X size={16} className="text-red-500 dark:text-red-400" />;
      case 'not_started':
        return <Circle size={16} className="text-gray-400 dark:text-gray-500" />;
      default:
        return null;
    }
  };

  return (
    <div
      className={`px-3 py-2 rounded-lg border-2 min-w-[160px] transition-all relative ${
        data.isActive
          ? `${getStatusColor(data.status)} shadow-lg ring-2 ring-offset-1 ring-blue-300 dark:ring-blue-700`
          : `${getStatusColor(data.status)} hover:shadow-md`
      } ${data.status === 'not_started' ? 'cursor-default' : 'cursor-pointer'}`}
    >
      {/* Input handle at top */}
      <Handle type="target" position={Position.Top} style={{ top: -5, background: '#6b7280' }} />

      <div className="flex items-center gap-2">
        <StatusIcon />
        <div className="flex-1">
          <div
            className={`font-medium text-sm truncate text-zinc-900 dark:text-zinc-100 ${getTextOpacity(data.status)}`}
          >
            {data.title}
          </div>
        </div>
      </div>

      {/* Output handle at bottom */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ bottom: -5, background: '#6b7280' }}
      />
    </div>
  );
};

// Custom GroupedTaskNode component
const GroupedTaskNode = ({ data }: { data: any }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'border-green-500 bg-green-50 dark:bg-green-900/30';
      case 'in_progress':
        return 'border-blue-500 bg-blue-50 dark:bg-blue-900/30';
      case 'scheduled':
        return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/30';
      case 'failed':
        return 'border-red-500 bg-red-50 dark:bg-red-900/30';
      case 'not_started':
        return 'border-gray-300 bg-gray-100 dark:border-gray-600 dark:bg-gray-800';
      default:
        return 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-zinc-900';
    }
  };

  const getOverallStatus = (tasks: Task[]) => {
    if (tasks.some((task) => task.status === 'failed')) return 'failed';
    if (tasks.some((task) => task.status === 'in_progress')) return 'in_progress';
    if (tasks.some((task) => task.status === 'scheduled')) return 'scheduled';
    if (tasks.every((task) => task.status === 'not_started')) return 'not_started';
    if (tasks.every((task) => task.status === 'completed')) return 'completed';
    return 'in_progress'; // Mixed states
  };

  const getTextOpacity = (status: string) => {
    return status === 'not_started' ? 'opacity-50' : 'opacity-100';
  };

  const StatusIcon = () => {
    const status = getOverallStatus(data.tasks);
    switch (status) {
      case 'completed':
        return <CheckCircle2 size={16} className="text-green-500 dark:text-green-400" />;
      case 'in_progress':
        return <Loader2 size={16} className="text-blue-500 dark:text-blue-400 animate-spin" />;
      case 'scheduled':
        return <Loader2 size={16} className="text-yellow-500 dark:text-yellow-400 animate-spin" />;
      case 'failed':
        return <X size={16} className="text-red-500 dark:text-red-400" />;
      case 'not_started':
        return <Circle size={16} className="text-gray-400 dark:text-gray-500" />;
      default:
        return null;
    }
  };

  const overallStatus = getOverallStatus(data.tasks);

  return (
    <div
      className={`px-3 py-2 rounded-lg border-2 min-w-[160px] transition-all relative ${
        data.isActive
          ? `${getStatusColor(overallStatus)} shadow-lg ring-2 ring-offset-1 ring-blue-300 dark:ring-blue-700`
          : `${getStatusColor(overallStatus)} hover:shadow-md`
      } ${overallStatus === 'not_started' ? 'cursor-default' : 'cursor-pointer'}`}
    >
      {/* Input handle at top */}
      <Handle type="target" position={Position.Top} style={{ top: -5, background: '#6b7280' }} />

      <div className="flex items-center gap-2">
        <StatusIcon />
        <div className="flex-1">
          <div
            className={`font-medium text-sm truncate text-zinc-900 dark:text-zinc-100 ${getTextOpacity(overallStatus)}`}
          >
            {data.title}
          </div>
          <div
            className={`text-xs text-zinc-600 dark:text-zinc-400 flex items-center gap-1 ${getTextOpacity(overallStatus)}`}
          >
            <Repeat size={12} />
            <span>{data.tasks.length} instances</span>
          </div>
        </div>
      </div>

      {/* Output handle at bottom */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ bottom: -5, background: '#6b7280' }}
      />
    </div>
  );
};

const nodeTypes: NodeTypes = {
  workflow: WorkflowNode,
  task: TaskNode,
  groupedTask: GroupedTaskNode,
};

// Layout nodes using dagre
const getLayoutedElements = (nodes: Node[], edges: Edge[]) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: 'TB', // Top to Bottom
    nodesep: 100, // Horizontal spacing between nodes
    ranksep: 100, // Vertical spacing between ranks
    align: 'UL', // Align to upper left
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 200, height: 80 });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      targetPosition: 'top' as any,
      sourcePosition: 'bottom' as any,
      position: {
        x: nodeWithPosition.x - 100,
        y: nodeWithPosition.y - 40,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

export const FlowVisualization: React.FC<FlowVisualizationProps> = ({
  workflowRun,
  activeItem,
  onItemClick,
}) => {
  // Create workflow item data
  const workflowItem = useMemo(
    (): SidebarItemData => ({
      id: 'workflow',
      title: 'Workflow',
      description: workflowRun.workflowName,
      status: workflowRun.status as
        | 'not_started'
        | 'scheduled'
        | 'in_progress'
        | 'completed'
        | 'failed',
      input: workflowRun.workflowInput,
      output: workflowRun.workflowOutput,
    }),
    [workflowRun]
  );

  // Generate nodes and edges
  const { initialNodes, initialEdges } = useMemo(() => {
    // Create workflow node
    const workflowNode: Node = {
      id: 'workflow',
      type: 'workflow',
      position: { x: 0, y: 0 },
      data: {
        ...workflowItem,
        isActive: activeItem.id === 'workflow',
      },
    };

    // Group tasks by title
    const groupedTasks = workflowRun.tasks.reduce(
      (acc, task) => {
        if (!acc[task.title]) {
          acc[task.title] = [];
        }
        acc[task.title].push(task);
        return acc;
      },
      {} as Record<string, Task[]>
    );

    // Create task nodes (grouped)
    const taskNodes: Node[] = Object.entries(groupedTasks).map(([title, tasks], index) => {
      const isGrouped = tasks.length > 1;
      return {
        id: isGrouped ? `group-${title}` : tasks[0].id,
        type: isGrouped ? 'groupedTask' : 'task',
        position: { x: 0, y: 100 + index * 100 },
        data: {
          ...tasks[0],
          title,
          tasks: isGrouped ? tasks : undefined,
          isActive: isGrouped
            ? tasks.some((t) => t.id === activeItem.id)
            : activeItem.id === tasks[0].id,
        },
      };
    });

    const nodes = [workflowNode, ...taskNodes];

    // Create edges
    const edges: Edge[] = [];

    // Add workflow to first task connection
    if (taskNodes.length > 0) {
      edges.push({
        id: 'e-workflow-first',
        source: 'workflow',
        target: taskNodes[0].id,
        type: 'default',
        animated: false,
        style: { stroke: '#6b7280', strokeWidth: 2 },
      });
    }

    // Add sequential task connections
    for (let i = 0; i < taskNodes.length - 1; i++) {
      edges.push({
        id: `e-task-${i}`,
        source: taskNodes[i].id,
        target: taskNodes[i + 1].id,
        type: 'default',
        animated: false,
        style: { stroke: '#6b7280', strokeWidth: 2 },
      });
    }

    const layouted = getLayoutedElements(nodes, edges);

    return {
      initialNodes: layouted.nodes,
      initialEdges: layouted.edges,
    };
  }, [workflowRun, workflowItem, activeItem.id]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Generate nodes and edges when workflowRun changes
  const { nodes: newNodes, edges: newEdges } = useMemo(() => {
    // Create workflow node
    const workflowNode: Node = {
      id: 'workflow',
      type: 'workflow',
      position: { x: 0, y: 0 },
      data: {
        ...workflowItem,
        isActive: activeItem.id === 'workflow',
      },
    };

    // Group tasks by title
    const groupedTasks = workflowRun.tasks.reduce(
      (acc, task) => {
        if (!acc[task.title]) {
          acc[task.title] = [];
        }
        acc[task.title].push(task);
        return acc;
      },
      {} as Record<string, Task[]>
    );

    // Create task nodes (grouped)
    const taskNodes: Node[] = Object.entries(groupedTasks).map(([title, tasks], index) => {
      const isGrouped = tasks.length > 1;
      return {
        id: isGrouped ? `group-${title}` : tasks[0].id,
        type: isGrouped ? 'groupedTask' : 'task',
        position: { x: 0, y: 100 + index * 100 },
        data: {
          ...tasks[0],
          title,
          tasks: isGrouped ? tasks : undefined,
          isActive: isGrouped
            ? tasks.some((t) => t.id === activeItem.id)
            : activeItem.id === tasks[0].id,
        },
      };
    });

    const nodes = [workflowNode, ...taskNodes];

    // Create edges
    const edges: Edge[] = [];

    // Add workflow to first task connection
    if (taskNodes.length > 0) {
      edges.push({
        id: 'e-workflow-first',
        source: 'workflow',
        target: taskNodes[0].id,
        type: 'default',
        animated: false,
        style: { stroke: '#6b7280', strokeWidth: 2 },
      });
    }

    // Add sequential task connections
    for (let i = 0; i < taskNodes.length - 1; i++) {
      edges.push({
        id: `e-task-${i}`,
        source: taskNodes[i].id,
        target: taskNodes[i + 1].id,
        type: 'default',
        animated: false,
        style: { stroke: '#6b7280', strokeWidth: 2 },
      });
    }

    return getLayoutedElements(nodes, edges);
  }, [workflowRun, workflowItem, activeItem.id]);

  // Update nodes when workflowRun changes
  React.useEffect(() => {
    setNodes(newNodes);
    setEdges(newEdges);
  }, [newNodes, newEdges, setNodes, setEdges]);

  // Update nodes when activeItem changes
  React.useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        data: {
          ...node.data,
          isActive: node.id === activeItem.id,
        },
      }))
    );
  }, [activeItem.id, setNodes]);

  // Handle node clicks
  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      // Don't allow interaction with not-started tasks
      if (node.data.status === 'not_started') {
        return;
      }

      if (node.id === 'workflow') {
        onItemClick(workflowItem);
      } else if (node.type === 'groupedTask' && 'tasks' in node.data) {
        // For grouped tasks, click the first task in the group that's not not_started
        const tasks = node.data.tasks as Task[];
        const interactableTask = tasks.find((t) => t.status !== 'not_started') || tasks[0];
        if (interactableTask.status !== 'not_started') {
          onItemClick(interactableTask);
        }
      } else {
        const task = workflowRun.tasks.find((t) => t.id === node.id);
        if (task && task.status !== 'not_started') {
          onItemClick(task);
        }
      }
    },
    [workflowItem, workflowRun.tasks, onItemClick]
  );

  return (
    <div className="h-full w-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        connectionMode={ConnectionMode.Loose}
        defaultEdgeOptions={{
          type: 'default',
          style: { stroke: '#6b7280', strokeWidth: 2 },
          animated: false,
        }}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        proOptions={{ hideAttribution: true }}
      >
        <Controls />
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
      </ReactFlow>
    </div>
  );
};
